import { AESCipherSIVDeterministic } from '../../../base/cipher/aes';
import { AbstractSelfAccountKeyring } from '../../../keyring/account_base';
import { AbstractCryptoConfig, ArgumentsError } from '../../base';
import { AccountMasterDeriveKeyV2 } from '../../common';
import { AccountRootKeyConfigDumpArgs, AccountRootKeyConfigLoadArgs } from './args';
import { makeInfoByteArray } from '../../../utility';
import { CryptoUpgrade } from '../../feature';

/**
 * Account key encryption: AES-SIV
        KEY: 32B from account keyring -> 64B HKDF by context
        NONCE: none

    Used to encrypt various keys of the account. (for public key crypto)
    In addition to V1, this also uses the self-keyring variant (pqc or not) for deriving a key, ensuring the same
    will be required at load. This guarantees that all keys are loaded for the client (if they use unique passwords
    in general, but especially after upgrading).
    NOTE: No LRU cache on derive_key(), root keys are only loaded once and they are unique so there's no benefit.
 */
export class AccountRootKeyConfigV2 extends AbstractCryptoConfig<
  AccountRootKeyConfigLoadArgs,
  AccountRootKeyConfigDumpArgs
> {
  constructor(cu: CryptoUpgrade) {
    super(2, cu);
  }

  public load(args: AccountRootKeyConfigLoadArgs): Promise<Uint8Array> {
    if (args.variant === AbstractSelfAccountKeyring.VARIANT_BASE) {
      throw new ArgumentsError("Can't load base variant keys with this version");
    }
    return this.derive_key(args.variant, args.self_kr, args.key_version).then((derive_key) => {
      return AESCipherSIVDeterministic.decrypt(
        args.cipher.slice(this.version.byteLength),
        derive_key
      );
    });
  }

  public dump(args: AccountRootKeyConfigDumpArgs): Promise<Uint8Array> {
    if (args.variant === AbstractSelfAccountKeyring.VARIANT_BASE) {
      throw new ArgumentsError("Can't dump base variant keys with this version");
    }
    return this.derive_key(args.variant, args.self_kr, args.key_version).then((derive_key) => {
      return AESCipherSIVDeterministic.encrypt(
        args.self_kr.get_secret_key(args.key_version),
        derive_key,
        undefined,
        this.version
      );
    });
  }

  public derive_key(variant: string, self_kr: AbstractSelfAccountKeyring, key_version: string) {
    return AccountMasterDeriveKeyV2.derive_key(
      self_kr,
      makeInfoByteArray(['account-root-key-', variant, '-', this.version, '-', key_version]),
      false
    );
  }
}
